/** Classifies a thrown pi-server AI-chat error (boundary-narrowed field-by-field) into a calm, user-facing in-chat descriptor. Never throws. */

import { SESSION_ENDED_MESSAGE, StreamResumeError, TruncatedStreamError } from "connected-components/ai-teammate/utils/chat-transport"

/** Expected "stream is gone" outcomes that need no logging (reattach/reconnect and cancelStream alike): a nav-away abort, or no/expired active stream (404/410). */
export function isExpectedStreamGoneError(error: unknown): boolean {
  if (error instanceof StreamResumeError) return true
  return typeof error === "object" && error !== null && (error as { name?: unknown }).name === "AbortError"
}

export const LOCAL_TURN_IN_PROGRESS_CODE = "CHAT_CLIENT_TURN_IN_PROGRESS"

export type ChatApiErrorKind = "rate-limit" | "unavailable" | "session-terminated" | "still-stopping" | "out-of-sync" | "not-available" | "unknown"

export interface ChatApiErrorDescriptor {
  kind: ChatApiErrorKind
  message: string
}

const MESSAGES: Record<ChatApiErrorKind, string> = {
  "rate-limit": "You've reached the usage limit for now. Please wait a moment and try again.",
  unavailable: "The assistant is temporarily unavailable. Please try again in a little while.",
  "session-terminated": SESSION_ENDED_MESSAGE,
  "still-stopping": "Your previous message is still finishing. Try again in a moment.",
  "out-of-sync": "This message is out of sync with the current thread. Please refresh the thread and try again.",
  "not-available": "This conversation is no longer available.",
  unknown: "Something went wrong on our end. Please try again.",
}

// Says only what the transport witnessed: the answer never arrived. Whether the server finished it is unknowable
// here, and where the reader picks it up depends on whether the view rolled back, which this layer cannot see.
export const INTERRUPTED_STREAM_MESSAGE = "The connection to Kelly dropped before this answer came through."

/**
 * A turn that died mid-flight, rather than one that never opened. Keeps a cause the classifier could place;
 * otherwise names the drop, which is the message the generic fallback failed to give (APP-3527). `streamed` is
 * the caller's witness that output had already arrived — a truncated stream is a drop whether or not it had.
 */
export function describeInterruptedStream(error: unknown, { streamed }: { streamed: boolean }): string {
  const described = describeChatApiError(error)
  if (described.kind !== "unknown") return described.message
  const droppedInFlight = streamed || error instanceof TruncatedStreamError
  return droppedInFlight ? INTERRUPTED_STREAM_MESSAGE : described.message
}

/** The numeric HTTP-ish `code` the client error classes stamp on themselves. */
function statusCodeOf(error: unknown): number | null {
  if (typeof error !== "object" || error === null || !("code" in error)) return null
  const { code } = error as { code: unknown }
  return typeof code === "number" ? code : null
}

/** The `retryAfter` header value `TooManyRequestsError` carries, when present. */
function retryAfterOf(error: unknown): string | null {
  if (typeof error !== "object" || error === null || !("retryAfter" in error)) return null
  const { retryAfter } = error as { retryAfter: unknown }
  return typeof retryAfter === "string" && retryAfter.length > 0 ? retryAfter : null
}

/** The op-summarize error `code` string riding on a `TechnicalError`/`ConflictError` body. */
function bodyCodeOf(error: unknown): string | null {
  if (typeof error !== "object" || error === null || !("error" in error)) return null
  const body: unknown = (error as { error: unknown }).error
  if (typeof body !== "object" || body === null || !("code" in body)) return null
  const { code } = body as { code: unknown }
  return typeof code === "string" ? code : null
}

/** The instant a daily budget window reopens, carried in the body's `details`. */
function resetsAtOf(error: unknown): string | null {
  if (typeof error !== "object" || error === null || !("error" in error)) return null
  const body: unknown = (error as { error: unknown }).error
  if (typeof body !== "object" || body === null || !("details" in body)) return null
  const details: unknown = (body as { details: unknown }).details
  if (typeof details !== "object" || details === null || !("resetsAt" in details)) return null
  const { resetsAt } = details as { resetsAt: unknown }
  return typeof resetsAt === "string" ? resetsAt : null
}

/** The budget window closes at midnight UTC, which is a later calendar day east of it — name the date unless the reset falls on the viewer's today. */
function formatResetAt(resetsAt: string | null): string | null {
  if (resetsAt === null) return null
  const resetDate = new Date(resetsAt)
  if (Number.isNaN(resetDate.getTime())) return null
  const time = resetDate.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit", timeZoneName: "short" })
  if (resetDate.toDateString() === new Date().toDateString()) return time
  return `${resetDate.toLocaleDateString(undefined, { month: "short", day: "numeric" })} at ${time}`
}

function matches(bodyCode: string | null, needle: string): boolean {
  return bodyCode != null && bodyCode.toUpperCase().includes(needle)
}

/**
 * op-summarize's turn-failure classes, mapped onto this module's taxonomy. The SSE `error` event names the
 * server exception; every other failure on this surface is classified from an HTTP response, so without this
 * the same failure would report one value over the streaming transport and another over the buffered one.
 *
 * Several classes collapse to `unavailable` — which is why the raw class is reported ALONGSIDE the kind
 * rather than instead of it: this vocabulary is what makes failures comparable, not what makes them specific.
 */
const KIND_BY_SERVER_ERROR_TYPE: Readonly<Record<string, ChatApiErrorKind>> = Object.assign(Object.create(null), {
  ManagedAgentRateLimitError: "rate-limit",
  ManagedAgentSessionTerminatedError: "session-terminated",
  ManagedAgentTurnCeilingError: "unavailable",
  ManagedAgentStreamStalledError: "unavailable",
  ManagedAgentStreamTimeoutError: "unavailable",
  ManagedAgentHostedToolTimeoutError: "unavailable",
  ManagedAgentAPIError: "unavailable",
  ManagedAgentNetworkError: "unavailable",
  ManagedAgentUpstreamFaultError: "unavailable",
  // Provider capacity, not the caller's quota: `rate-limit` would read as the user hitting their own limit.
  ManagedAgentOverloadedError: "unavailable",
  ManagedAgentStreamOverloadedError: "unavailable",
  ManagedAgentRetriesExhaustedError: "unavailable",
  ManagedAgentFallbackOverloadError: "unavailable",
  ManagedAgentError: "unavailable",
})

/** The taxonomy bucket for a server exception class, or `null` for one this app has no opinion about yet. */
export function chatApiErrorKindForServerType(errorType: string | null): ChatApiErrorKind | null {
  if (errorType == null) return null
  return KIND_BY_SERVER_ERROR_TYPE[errorType] ?? null
}

/** Classify a thrown chat API error into a calm descriptor. Never throws. */
export function describeChatApiError(error: unknown): ChatApiErrorDescriptor {
  const status = statusCodeOf(error)
  const bodyCode = bodyCodeOf(error)

  // Rate limit / token budget: the 429 status, or an explicit budget/rate code on the body.
  if (status === 429 || matches(bodyCode, "RATE_LIMIT") || matches(bodyCode, "BUDGET")) {
    const resetAt = formatResetAt(resetsAtOf(error))
    if (resetAt != null) {
      return { kind: "rate-limit", message: `You've reached your daily usage limit. You can send more messages after ${resetAt}.` }
    }
    const retryAfter = retryAfterOf(error)
    const seconds = retryAfter != null ? Number.parseInt(retryAfter, 10) : Number.NaN
    if (Number.isFinite(seconds) && seconds > 0) {
      return { kind: "rate-limit", message: `You've reached the usage limit. Please try again in about ${seconds} seconds.` }
    }
    return { kind: "rate-limit", message: MESSAGES["rate-limit"] }
  }

  // A genuinely ended session: an explicit session/terminated code on the body (checked before 409, which is recoverable).
  if (matches(bodyCode, "SESSION") || matches(bodyCode, "TERMINAT")) {
    return { kind: "session-terminated", message: MESSAGES["session-terminated"] }
  }

  if (matches(bodyCode, "TURN_IN_PROGRESS")) {
    return { kind: "still-stopping", message: MESSAGES["still-stopping"] }
  }

  // Out-of-sync continue cursor (409 conflict): the thread moved on, recoverable by refreshing — NOT a dead session.
  if (status === 409 || matches(bodyCode, "CONFLICT")) {
    return { kind: "out-of-sync", message: MESSAGES["out-of-sync"] }
  }

  // Deleted / inaccessible thread.
  if (status === 404 || matches(bodyCode, "NOT_FOUND")) {
    return { kind: "not-available", message: MESSAGES["not-available"] }
  }

  // Service unavailable / any other server-side failure (503, 5xx, TechnicalError).
  if (status != null && status >= 500) {
    return { kind: "unavailable", message: MESSAGES.unavailable }
  }
  if (matches(bodyCode, "UNAVAILABLE")) {
    return { kind: "unavailable", message: MESSAGES.unavailable }
  }

  return { kind: "unknown", message: MESSAGES.unknown }
}

/**
 * The server's own failure class, bounded before it reaches the analytics vendor.
 * Bounded by SHAPE, not an allowlist: an unrecognised class must still get through, which is what this exists for.
 */
const SERVER_ERROR_TYPE = /^[A-Za-z0-9_.-]{1,64}$/

export function safeErrorType(errorType: unknown): string | undefined {
  return typeof errorType === "string" && SERVER_ERROR_TYPE.test(errorType) ? errorType : undefined
}
